import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { v2 } from "oc-threejs/short3";

import _ from "lodash";
import { Material, Matrix3 } from "three";

// Für ModelGhost3d, ermöglicht das Shuffeln von Texturen, damit z.B. Profile / Dekore
// nicht gleichartig übereinandergestapelt werden

interface IShuffleAxis {
  s: number;
  l: number;
}

export const calculateMaterialUvTransformShuffles = (
  material: Material,
  shuffleAxisArray: number[] | null | undefined,
): Matrix3[] => {
  if (shuffleAxisArray == null) {
    return [new Matrix3()];
  }
  const [u, v]: IShuffleAxis[] = shuffleAxisArray.map((val: number) => ({
    s: val < 0 ? 2 : 1,
    l: Math.max(1, Math.round(Math.abs(val))),
  }));

  const shuffles: Matrix3[] = [];
  const f = (axis: IShuffleAxis, index: number): number => (axis.l < 2 ? 0 : index / axis.l);

  for (let um = 0; um < u.s; um++) {
    for (let vm = 0; vm < v.s; vm++) {
      for (let ui = 0; ui < u.l; ui++) {
        for (let vi = 0; vi < v.l; vi++) {
          const offset = v2(f(u, ui), f(v, vi));
          const rotation = um === 1 || vm === 1 ? Math.PI : 0;
          const m = new Matrix3();
          m.setUvTransform(offset.x, offset.y, 1, 1, rotation, 0, 0);
          shuffles.push(m);
        }
      }
    }
  }
  return _.shuffle(shuffles);
};

const getShuffleAxisArray = (
  material: Material,
  shuffleMap: Record<string, number[]> | undefined,
): number[] => {
  const name = material.userData.originalName ?? material.name;
  const shuffleAxisArray = shuffleMap?.[name] ?? [0, 0];
  return shuffleAxisArray;
};

const cacheMaterialUvTransformShuffles = Commons.cacheIdentity();

const getCachedUvTransformShuffles = (
  material: Material,
  shuffleMap: Record<string, number[]> | undefined,
  mmrId: number,
): Matrix3[] =>
  cacheMaterialUvTransformShuffles(Maths.pairing(material.id, mmrId), () =>
    calculateMaterialUvTransformShuffles(material, getShuffleAxisArray(material, shuffleMap)),
  );

export interface IGetUvTransformOptions {
  shuffleMap?: Record<string, number[]>;
  shuffleMaterialIndex: number;
  mmrId: number;
}

export const getUvTransform = (
  material: Material,
  { shuffleMap, shuffleMaterialIndex, mmrId }: IGetUvTransformOptions,
): Matrix3 => {
  const shuffles = getCachedUvTransformShuffles(material, shuffleMap, mmrId);
  const result = shuffles[shuffleMaterialIndex % shuffles.length];
  return result;
};

const _getInstanceUvTransform_m = new Matrix3();

export interface IInstanceWithUvOffset {
  uvOffset: { x: number; y: number };
}

export const getInstanceUvTransform = (
  instance: IInstanceWithUvOffset,
  materialUvTransform: Matrix3,
): Matrix3 => {
  const offset = instance.uvOffset;
  _getInstanceUvTransform_m.setUvTransform(offset.x, offset.y, 1, 1, 0, 0, 0);
  _getInstanceUvTransform_m.multiplyMatrices(materialUvTransform, _getInstanceUvTransform_m);
  return _getInstanceUvTransform_m;
};
